import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';

import { en } from '../../locale';

export type ConfirmVariant = 'warning' | 'error' | 'info' | 'success';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  variant?: ConfirmVariant;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmColor?: 'primary' | 'error' | 'warning' | 'success';
  loading?: boolean;
}

const variantConfig = {
  warning: {
    icon: WarningIcon,
    color: '#fbbf24',
    bgColor: 'rgba(251,191,36,0.08)',
    borderColor: 'rgba(251,191,36,0.2)',
  },
  error: {
    icon: ErrorIcon,
    color: '#ef4444',
    bgColor: 'rgba(239,68,68,0.08)',
    borderColor: 'rgba(239,68,68,0.2)',
  },
  info: {
    icon: InfoIcon,
    color: '#4a90e2',
    bgColor: 'rgba(74,144,226,0.08)',
    borderColor: 'rgba(74,144,226,0.2)',
  },
  success: {
    icon: SuccessIcon,
    color: '#22c55e',
    bgColor: 'rgba(34,197,94,0.08)',
    borderColor: 'rgba(34,197,94,0.2)',
  },
};

/**
 * Dialogue de confirmation réutilisable
 * Supporte différents variants (warning, error, info, success)
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  variant = 'warning',
  confirmText = en.common.confirm,
  cancelText = en.common.cancel,
  onConfirm,
  onCancel,
  confirmColor = 'error',
  loading = false,
}: ConfirmDialogProps) {
  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: '10px',
              backgroundColor: config.bgColor,
              border: `1px solid ${config.borderColor}`,
            }}
          >
            <Icon sx={{ fontSize: 24, color: config.color }} />
          </Box>
          <span>{title}</span>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
          {message}
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} disabled={loading} color="inherit">
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading}
          color={confirmColor}
          sx={{
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          {loading ? en.common.loading : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
